import type { SupplierCatalogEntry } from "./api";

// Tabella read-only del catalogo dimensioni fornitori (#2 legacy parity),
// estratta da FornitoriPage per testabilità isolata + file più piccoli.
//
// Multi-fornitore, tre modalità sugli stessi dati:
// - DIMENSIONALI (Itamoby/Ityhome/Cosma): colli + peso + L/P/A + bancale.
// - AllService "sola giacenza": colonne dimensionali a "—", Giacenza piena.
//   È un comportamento voluto, non un dato mancante.
// - COMMERCIALE (Bizzotto, entries con ``extra``): Nome/Categoria/Prezzo/EAN
//   al posto delle colonne colli — i "colli" Bizzotto sono dimensioni
//   prodotto con kg 0, non colli spedizione, quindi mostrarli confonderebbe.

const PLACEHOLDER = "—";

interface CatalogEntriesTableProps {
  items: SupplierCatalogEntry[];
  totalUnfiltered: number;
  /** Testo alternativo dell'empty state (default: suggerisce "Carica Excel"). */
  emptyHint?: string;
  /** Primo fetch in corso: placeholder neutro invece del falso empty-state. */
  loading?: boolean;
}

export function CatalogEntriesTable({
  items,
  totalUnfiltered,
  emptyHint,
  loading = false,
}: CatalogEntriesTableProps) {
  // Caricamento: evita il flash di "Nessun SKU / Importa" ad ogni cambio tab
  // mentre il fetch e' in volo (Bizzotto ha 3605 SKU reali).
  if (loading && totalUnfiltered === 0) {
    return (
      <div className="border-t border-line px-4 py-4 text-[12px] italic text-ink-2">
        Caricamento catalogo…
      </div>
    );
  }
  if (totalUnfiltered === 0) {
    return (
      <div className="border-t border-line px-4 py-4 text-[12px] text-ink-2">
        <p className="mb-1.5 font-medium text-ink-2">Nessun SKU in catalogo.</p>
        <p className="text-[11.5px] leading-relaxed">
          {emptyHint ?? (
            <>
              Senza i dati di questo listino le LDV non possono essere generate automaticamente:
              tutti gli ordini dovrebbero avere peso/dimensioni inseriti a mano. Clicca{" "}
              <strong>"Carica Excel"</strong> qui sopra per uploadare l'ultimo listino del
              fornitore.
            </>
          )}
        </p>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="border-t border-line px-4 py-3 text-[11.5px] italic text-ink-2">
        Nessun SKU corrisponde al filtro.
      </div>
    );
  }
  // Modalità commerciale se almeno una entry porta ``extra`` (Bizzotto:
  // catalogo omogeneo, o tutte o nessuna).
  const hasExtra = items.some((it) => it.extra != null);
  return (
    <div className="border-t border-line max-h-[360px] overflow-y-auto">
      <table className="w-full tabular-nums text-[11px]">
        <thead className="sticky top-0 bg-paper-2 text-ink-2">
          {hasExtra ? (
            <tr>
              <th className="text-left px-3 py-1.5">SKU</th>
              <th className="text-left px-3 py-1.5">Nome</th>
              <th className="text-left px-3 py-1.5">Categoria</th>
              <th className="text-right px-3 py-1.5">Prezzo</th>
              <th className="text-right px-3 py-1.5">EAN Bizzotto</th>
              <th className="text-right px-3 py-1.5">EAN BSCOM</th>
              <th className="text-left px-3 py-1.5">Fonte</th>
            </tr>
          ) : (
            <tr>
              <th className="text-left px-3 py-1.5">SKU</th>
              <th className="text-right px-3 py-1.5">Colli</th>
              <th className="text-right px-3 py-1.5">Peso tot (kg)</th>
              <th className="text-right px-3 py-1.5">Max L/P/A (cm)</th>
              <th className="text-right px-3 py-1.5">Bancale</th>
              <th className="text-right px-3 py-1.5">Giacenza</th>
              <th className="text-left px-3 py-1.5">Fonte</th>
            </tr>
          )}
        </thead>
        <tbody>
          {items.slice(0, 100).map((it) => (hasExtra ? extraRow(it) : dimensionalRow(it)))}
          {totalUnfiltered > items.length && (
            <tr>
              <td colSpan={7} className="px-3 py-1.5 text-center italic text-ink-2">
                ... e altri {totalUnfiltered - items.length} SKU (filtra per nome)
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function dimensionalRow(it: SupplierCatalogEntry) {
  // Entries dimensionali: colli valorizzati. AllService: colli vuoti
  // → placeholder sulle colonne dimensionali invece di 0.
  const hasDimensions = it.colli.length > 0;
  const totalKg = it.colli.reduce((acc, c) => acc + (c.kg || 0), 0);
  const maxL = it.colli.reduce((acc, c) => Math.max(acc, c.l || 0), 0);
  const maxP = it.colli.reduce((acc, c) => Math.max(acc, c.p || 0), 0);
  const maxA = it.colli.reduce((acc, c) => Math.max(acc, c.a || 0), 0);
  return (
    <tr key={it.sku} className="border-t border-line/40 hover:bg-paper-2">
      <td className="px-3 py-1 text-ink">{it.sku}</td>
      <td className="px-3 py-1 text-right text-ink-2">
        {hasDimensions ? it.n_colli : PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {hasDimensions ? totalKg.toFixed(1) : PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {hasDimensions ? `${maxL.toFixed(0)}×${maxP.toFixed(0)}×${maxA.toFixed(0)}` : PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-right text-ink-2">{it.bancale ? "✓" : PLACEHOLDER}</td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {it.giacenza > 0 ? it.giacenza : PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-ink-2 truncate max-w-[260px]" title={it.fonte}>
        {it.fonte}
      </td>
    </tr>
  );
}

function extraRow(it: SupplierCatalogEntry) {
  const extra = it.extra ?? {};
  const prezzo = typeof extra.prezzo === "number" && extra.prezzo > 0 ? extra.prezzo : null;
  return (
    <tr key={it.sku} className="border-t border-line/40 hover:bg-paper-2">
      <td className="px-3 py-1 text-ink">{it.sku}</td>
      <td className="px-3 py-1 text-ink-2 truncate max-w-[280px]" title={extra.nome ?? ""}>
        {extra.nome || PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-ink-2">{extra.categoria || PLACEHOLDER}</td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {prezzo !== null
          ? prezzo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })
          : PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {extra.ean_bizzotto || PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-right text-ink-2 tabular-nums">
        {extra.ean_bscom || PLACEHOLDER}
      </td>
      <td className="px-3 py-1 text-ink-2 truncate max-w-[260px]" title={it.fonte}>
        {it.fonte}
      </td>
    </tr>
  );
}
